import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  boxIngredients,
  formatQuantity,
  type BoxSize,
  type PakketResponse,
  type RecipeIngredient,
  type SuggestedRecipe,
} from '@familyhub/shared';
import { api, ApiError, isOffline } from '../api/client';
import { useSession } from '../session';
import { RecipeSheet } from './RecipeSheet';
import { AddToListSheet } from './AddToListSheet';
import { startGeneration, getGeneration, latestGeneration, adjustRecipe } from './suggest';
import type { RecipeInput } from './actions';
import { DotsMini, Skeleton } from '../components/Loading';
import { GearIcon } from '../components/GearIcon';

/**
 * Generate recipes for the week from the pakket box — a two-step flow.
 *
 * A recepten-module screen (it saves through recepten's own outbox), reached from
 * the Pakket screen — which hands the box over as `ingredients` in router state, a
 * URL/nav seam, never an import. Opened without that (from the import screen) it
 * best-effort fetches the current box itself.
 *
 * Step 1 (setup) is one compact form: which produce to cook with (multi-select
 * chips + search/add), a few quick preference toggles, and steppers for portions
 * and how many recipes. Step 2 (results) is the generated cards, with a staged
 * "what the model is doing" transition between the two. Opening a card is step 3,
 * an in-screen detail where the recipe is read in full and revised by chat.
 *
 * Generation is online-only and ephemeral, like the Pakket screen it springs
 * from: the batch lives in memory (and server staging) and survives re-mounts, but
 * only an *accepted* recipe is persisted (into recepten, then plannable).
 */

/** How often the screen polls a pending generation. */
const POLL_INTERVAL_MS = 2500;

/** What the model is doing while you wait — a staged progress, not a spinner. */
const GEN_STAGES = [
  'Ingrediënten analyseren…',
  'Combinaties zoeken…',
  'Recepten samenstellen…',
  'Bijna klaar…',
];

/** The quick preference toggles. Each maps to a fenced-as-data line in `wishes`. */
type Pref = 'snel' | 'vega' | 'vis' | 'mild';
const PREFS: { key: Pref; label: string; icon: ReactNode; wish: string }[] = [
  { key: 'snel', label: 'Snel', icon: <IconBolt />, wish: 'Snelle gerechten, klaar in ongeveer 20 minuten.' },
  { key: 'vega', label: 'Vega', icon: <IconSprout />, wish: 'Vegetarisch.' },
  { key: 'vis', label: 'Vis', icon: <IconFish />, wish: 'Graag (ook) een gerecht met vis.' },
  { key: 'mild', label: 'Mild', icon: <IconChili />, wish: 'Niet te pittig, mild op smaak.' },
];
/** Diet is one axis: vega and vis can't both be on. */
const EXCLUSIVE: Partial<Record<Pref, Pref>> = { vega: 'vis', vis: 'vega' };

/**
 * Survives re-mounts within a session — the household's inputs and last batch, so a
 * phone reopened mid-flow lands where it left off (the server stores the batch too).
 */
const sess: {
  pool: string[] | null;
  selected: string[] | null;
  prefs: Pref[];
  extra: string;
  servings: number;
  count: number;
  recipes: SuggestedRecipe[] | null;
  generationId: string | null;
  /** The ingredients the current batch was generated from — the "van 8" denominator. */
  generatedFrom: string[];
} = {
  pool: null,
  selected: null,
  prefs: [],
  extra: '',
  servings: 2,
  count: 3,
  recipes: null,
  generationId: null,
  generatedFrom: [],
};

function amountLabel(ing: RecipeIngredient): string {
  if (ing.quantity == null) return '';
  return ing.unit ? `${formatQuantity(ing.quantity)} ${ing.unit}` : formatQuantity(ing.quantity);
}

function suggestedToInput(r: SuggestedRecipe, sourceName: string): RecipeInput {
  return {
    title: r.title,
    servings: r.servings,
    total_minutes: r.total_minutes,
    ingredients: r.ingredients,
    steps: r.steps,
    tags: r.tags,
    // Generated text, tagged with where it came from so the overview can filter it.
    source_type: 'text',
    source_url: null,
    source_name: sourceName,
    image_path: null,
    source_images: [],
    notes: null,
  };
}

/** Fold the chosen toggles + free text into the model's batch directions. */
function composeWishes(prefs: Set<Pref>, extra: string): string | null {
  const parts = PREFS.filter((p) => prefs.has(p.key)).map((p) => p.wish);
  const e = extra.trim();
  if (e) parts.push(e);
  return parts.length ? parts.join(' ') : null;
}

export function RecipeSuggestScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : null;

  const navState = (location.state ?? null) as
    | { ingredients?: string[]; week?: number | null; size?: BoxSize }
    | null;
  const fromPakket = Boolean(navState?.ingredients);
  const week = navState?.week ?? null;
  const sourceName = week != null ? `Pakket wk ${week}` : 'Pakket';

  const [pool, setPool] = useState<string[]>(navState?.ingredients ?? sess.pool ?? []);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(navState?.ingredients ?? sess.selected ?? sess.pool ?? []),
  );
  const [query, setQuery] = useState('');
  const [prefs, setPrefs] = useState<Set<Pref>>(() => new Set(sess.prefs));
  const [extra, setExtra] = useState(sess.extra);
  const [servings, setServings] = useState(sess.servings);
  const [count, setCount] = useState(sess.count);

  const [recipes, setRecipes] = useState<SuggestedRecipe[] | null>(sess.recipes);
  const [generatedFrom, setGeneratedFrom] = useState<string[]>(sess.generatedFrom);
  const [status, setStatus] = useState<'idle' | 'generating' | 'error' | 'offline'>(
    sess.generationId && !sess.recipes ? 'generating' : 'idle',
  );
  const [generationId, setGenerationId] = useState<string | null>(sess.generationId);
  const [errorMsg, setErrorMsg] = useState('');
  const [step, setStep] = useState<'setup' | 'results'>(() =>
    !fromPakket && (sess.recipes || (sess.generationId && !sess.recipes)) ? 'results' : 'setup',
  );
  const [detailIndex, setDetailIndex] = useState<number | null>(null);

  // The accept flow (review → save → add-to-list), hoisted so a card's bookmark and
  // the detail view's "Bewaar" share one path. `saved` marks a card as bookmarked.
  const [accept, setAccept] = useState<number | null>(null);
  const [addList, setAddList] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [saved, setSaved] = useState<Set<number>>(new Set());

  const selectedList = useMemo(() => pool.filter((p) => selected.has(p)), [pool, selected]);

  /** A finished batch just came back — show it, cache it, land on results. */
  const applyReady = (got: SuggestedRecipe[], from: string[]) => {
    sess.recipes = got;
    sess.generatedFrom = from;
    setRecipes(got);
    setGeneratedFrom(from);
    setStatus('idle'); // stops the poll effect
    setStep('results');
  };

  // Best-effort prefill from the current box when opened without one (the import
  // screen's "Genereer uit je pakket"). Never fails the screen — you can type.
  useEffect(() => {
    if (fromPakket || pool.length > 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const box = await api<PakketResponse>('/api/v1/pakket');
        if (cancelled) return;
        const items = boxIngredients(box, navState?.size ?? 'middel');
        setPool(items);
        setSelected(new Set(items));
      } catch {
        /* leave empty; the field is editable */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once, on mount.

  }, []);

  // Resume the household's latest generation on reopen: the batch is stored
  // server-side, so a spinner started on another device — or before the app was
  // closed — is picked up here rather than lost. Best-effort. Skipped when this
  // session already has a batch in memory, or when we arrived fresh from Pakket
  // (that is an explicit request to cook from *this* box, not the old batch).
  useEffect(() => {
    if (sess.recipes || sess.generationId || fromPakket) return;
    let cancelled = false;
    void (async () => {
      try {
        const gen = await latestGeneration();
        if (cancelled || !gen) return;
        sess.generationId = gen.id;
        setGenerationId(gen.id);
        setPool((prev) => (prev.length > 0 ? prev : gen.ingredients));
        setSelected((prev) => (prev.size > 0 ? prev : new Set(gen.ingredients)));
        setGeneratedFrom(gen.ingredients);
        if (gen.wishes) setExtra((prev) => prev || gen.wishes || '');
        setServings(gen.servings);
        setCount(gen.count);
        if (gen.status === 'ready' && gen.recipes) applyReady(gen.recipes, gen.ingredients);
        else if (gen.status === 'pending') {
          setStatus('generating');
          setStep('results');
        }
        // 'failed' → leave on setup; the inputs are restored so a retry is one tap.
      } catch {
        /* offline or none — the screen is fully usable without it */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once, on mount.

  }, []);

  // Poll a pending batch until it is ready or failed. A transient poll error is
  // swallowed and the next tick retries — the generation is safe on the server.
  useEffect(() => {
    if (status !== 'generating' || !generationId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const gen = await getGeneration(generationId);
        if (cancelled) return;
        if (gen.status === 'ready' && gen.recipes) applyReady(gen.recipes, gen.ingredients);
        else if (gen.status === 'failed') {
          setErrorMsg(gen.error || 'Kon geen recepten maken. Probeer het opnieuw.');
          setStatus('error');
          setStep('setup');
        }
      } catch {
        /* keep polling */
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [status, generationId]);

  const toggleIngredient = (name: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const togglePref = (key: Pref) =>
    setPrefs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else {
        next.add(key);
        const other = EXCLUSIVE[key];
        if (other) next.delete(other);
      }
      return next;
    });

  const q = query.trim();
  const filtered = q ? pool.filter((p) => p.toLowerCase().includes(q.toLowerCase())) : pool;
  const canAdd = q.length > 0 && !pool.some((p) => p.toLowerCase() === q.toLowerCase());
  const addFromQuery = () => {
    if (!canAdd) return;
    setPool((prev) => [...prev, q]);
    setSelected((prev) => new Set(prev).add(q));
    setQuery('');
  };
  const allSelected = pool.length > 0 && selected.size >= pool.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pool));

  const canGenerate =
    status !== 'generating' && (selectedList.length > 0 || prefs.size > 0 || extra.trim().length > 0);

  const generate = async () => {
    const from = selectedList;
    const wishes = composeWishes(prefs, extra);
    // Persist the inputs so a re-mount restores the form.
    sess.pool = pool;
    sess.selected = from;
    sess.prefs = [...prefs];
    sess.extra = extra;
    sess.servings = servings;
    sess.count = count;
    sess.generatedFrom = from;

    setStep('results');
    setStatus('generating');
    setErrorMsg('');
    setRecipes(null);
    setGeneratedFrom(from);
    sess.recipes = null;
    // Null the id first so the poll effect can't fire against the previous
    // (already-ready) batch in the window before the new id lands.
    setGenerationId(null);
    sess.generationId = null;
    try {
      const gen = await startGeneration({
        ingredients: from,
        wishes,
        count,
        servings,
        source_name: sourceName,
      });
      sess.generationId = gen.id;
      setGenerationId(gen.id);
      // Almost always 'pending' — the poll effect (keyed on generationId) takes it
      // from here — but handle an instant answer too.
      if (gen.status === 'ready' && gen.recipes) applyReady(gen.recipes, from);
      else if (gen.status === 'failed') {
        setErrorMsg(gen.error || 'Kon geen recepten maken. Probeer het opnieuw.');
        setStatus('error');
        setStep('setup');
      }
    } catch (err) {
      setStep('setup');
      if (isOffline(err)) setStatus('offline');
      else {
        setErrorMsg(err instanceof ApiError ? err.message : 'Er ging iets mis.');
        setStatus('error');
      }
    }
  };

  const replaceRecipe = (index: number, next: SuggestedRecipe) =>
    setRecipes((prev) => {
      if (!prev) return prev;
      const copy = prev.slice();
      copy[index] = next;
      sess.recipes = copy;
      return copy;
    });

  const bookmark = (index: number) => setAccept(index);

  const detail = detailIndex != null && recipes ? recipes[detailIndex] : null;
  const backTarget = fromPakket ? '/pakket' : '/recepten';

  const acceptFlow = (
    <>
      {accept != null && recipes && recipes[accept] && (
        <RecipeSheet
          recipe={null}
          seed={suggestedToInput(recipes[accept]!, sourceName)}
          userId={userId}
          onClose={() => setAccept(null)}
          onSaved={(id) => {
            setSaved((prev) => new Set(prev).add(accept));
            setSavedId(id);
            setAddList(accept);
            setAccept(null);
          }}
        />
      )}
      {addList != null && recipes && recipes[addList] && (
        <AddToListSheet
          title={recipes[addList]!.title}
          ingredients={recipes[addList]!.ingredients}
          have={recipes[addList]!.uses_from_box}
          onClose={() => {
            setAddList(null);
            if (savedId) navigate(`/recepten/${savedId}`);
          }}
        />
      )}
    </>
  );

  // Step 3 — the in-screen recipe detail, where a suggestion is read in full and
  // revised by chat. Ephemeral (no id yet), so it is a view, not a route.
  if (detail && detailIndex != null) {
    return (
      <div className="app">
        <RecipeDetailView
          recipe={detail}
          index={detailIndex}
          boxTotal={generatedFrom.length}
          saved={saved.has(detailIndex)}
          onBack={() => setDetailIndex(null)}
          onReplace={replaceRecipe}
          onBookmark={() => bookmark(detailIndex)}
        />
        {acceptFlow}
      </div>
    );
  }

  const generating = step === 'results' && status === 'generating';

  return (
    <div className="app">
      <div className="topbar">
        {step === 'results' ? (
          <button className="btn btn-quiet back" onClick={() => setStep('setup')} aria-label="Terug naar de invoer">
            ‹ Terug
          </button>
        ) : (
          <Link className="btn btn-quiet back" to={backTarget} aria-label="Terug">
            ‹ {fromPakket ? 'Pakket' : 'Recepten'}
          </Link>
        )}
        <h1>{step === 'results' ? 'Recepten' : 'Uit je pakket'}</h1>
        {step === 'setup' && (
          <Link className="btn btn-quiet" to="/recepten/instellingen" aria-label="Recept-AI">
            <GearIcon />
          </Link>
        )}
      </div>

      <main className="stack">
        {step === 'setup' ? (
          <>
            <section className="card setup-card">
              <div className="setup-head">
                <h2 className="setup-title">Groente &amp; fruit</h2>
                {pool.length > 0 && (
                  <button type="button" className="btn btn-quiet setup-all" onClick={toggleAll} aria-pressed={allSelected}>
                    {allSelected ? 'Alles uit' : 'Alles aan'}
                  </button>
                )}
              </div>
              <p className="setup-sub">
                Wat er in de recepten mag —{' '}
                <b className="setup-count">{selectedList.length}</b> van {pool.length} gekozen.
              </p>

              <div className="ing-search">
                <IconSearch />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addFromQuery();
                    }
                  }}
                  placeholder="Zoek of voeg iets toe…"
                  aria-label="Ingrediënt zoeken of toevoegen"
                />
              </div>

              <div className="ing-chips" role="group" aria-label="Ingrediënten uit het pakket">
                {filtered.map((item) => {
                  const on = selected.has(item);
                  return (
                    <button
                      type="button"
                      key={item}
                      className="chip ing-chip"
                      aria-pressed={on}
                      onClick={() => toggleIngredient(item)}
                    >
                      <span className="ing-chip-mark" aria-hidden>
                        {on ? <IconCheck /> : <IconPlus />}
                      </span>
                      {item}
                    </button>
                  );
                })}
                {canAdd && (
                  <button type="button" className="chip ing-chip is-add" onClick={addFromQuery}>
                    <span className="ing-chip-mark" aria-hidden>
                      <IconPlus />
                    </span>
                    “{q}” toevoegen
                  </button>
                )}
                {filtered.length === 0 && !canAdd && <p className="setup-empty">Geen ingrediënten — voeg er een toe.</p>}
              </div>
            </section>

            <section className="card setup-card">
              <h2 className="setup-title">Voorkeuren</h2>
              <p className="setup-sub">Optioneel — stuur de hele reeks bij.</p>
              <div className="pref-grid" role="group" aria-label="Voorkeuren">
                {PREFS.map((p) => (
                  <button
                    type="button"
                    key={p.key}
                    className="chip pref-chip"
                    aria-pressed={prefs.has(p.key)}
                    onClick={() => togglePref(p.key)}
                  >
                    <span className="pref-chip-icon" aria-hidden>
                      {p.icon}
                    </span>
                    {p.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                className="pref-extra"
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                placeholder="Iets specifieks? Bijv. gebruik vooral de courgette"
                aria-label="Extra wens"
              />
            </section>

            <section className="card setup-card">
              <div className="setup-steppers">
                <Stepper label="Porties" value={servings} min={1} max={100} onChange={setServings} />
                <Stepper label="Aantal recepten" value={count} min={1} max={6} onChange={setCount} />
              </div>
            </section>

            {status === 'offline' && (
              <p className="banner">Geen verbinding — recepten maken lukt alleen online.</p>
            )}
            {status === 'error' && <p className="banner banner-error">{errorMsg}</p>}

            <button className="btn btn-primary btn-lg setup-cta" onClick={() => void generate()} disabled={!canGenerate}>
              Recepten maken <IconArrowRight />
            </button>
          </>
        ) : generating ? (
          <>
            <GenerateProgress />
            {Array.from({ length: Math.max(1, count) }).map((_, i) => (
              <SuggestionSkeleton key={i} />
            ))}
          </>
        ) : (
          <>
            {recipes && recipes.length === 0 && (
              <p className="empty">Geen recepten teruggekregen. Pas je keuze aan en probeer opnieuw.</p>
            )}

            {recipes?.map((recipe, i) => (
              <SuggestionCard
                key={i}
                recipe={recipe}
                boxTotal={generatedFrom.length}
                saved={saved.has(i)}
                onOpen={() => setDetailIndex(i)}
                onBookmark={() => bookmark(i)}
              />
            ))}

            {recipes && recipes.length > 0 && (
              <button className="btn btn-outline regen-btn" onClick={() => void generate()}>
                <IconRefresh /> Andere recepten genereren
              </button>
            )}
          </>
        )}
      </main>

      {acceptFlow}
    </div>
  );
}

/** A labelled −/+ stepper for a small integer count. */
function Stepper({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="stepper-field">
      <span className="sheet-label">{label}</span>
      <div className="stepper">
        <button
          type="button"
          className="btn btn-quiet"
          aria-label={`${label}: minder`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          −
        </button>
        <span className="stepper-value">{value}</span>
        <button
          type="button"
          className="btn btn-quiet"
          aria-label={`${label}: meer`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
        >
          +
        </button>
      </div>
    </div>
  );
}

/** The wait between step 1 and step 2: the model's stages, lighting up in turn. */
function GenerateProgress() {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    // Advance on a timer toward the last stage, then hold there until the result
    // lands. It signals activity, never a measured percentage.
    const id = setInterval(() => setStage((s) => Math.min(s + 1, GEN_STAGES.length - 1)), 7000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="gen-progress card" role="status" aria-label={GEN_STAGES[Math.min(stage, GEN_STAGES.length - 1)]}>
      <ul className="gen-stages">
        {GEN_STAGES.map((label, i) => {
          const state = i < stage ? 'done' : i === stage ? 'active' : 'pending';
          return (
            <li key={i} className={`gen-stage is-${state}`}>
              <span className="gen-stage-mark" aria-hidden>
                {state === 'done' ? (
                  <IconCheck />
                ) : state === 'active' ? (
                  <span className="thinking-dots">
                    <span />
                    <span />
                    <span />
                  </span>
                ) : (
                  <span className="gen-stage-dot" />
                )}
              </span>
              <span className="gen-stage-label">{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A suggestion card on its way — the same shape a real one lands in. */
function SuggestionSkeleton() {
  return (
    <article className="card rc-card sk-card" aria-hidden>
      <Skeleton className="sk-chip" w={128} h={26} />
      <Skeleton w="70%" h={20} />
      <Skeleton w={110} h={13} />
      <div className="sk-chip-row">
        <Skeleton className="sk-chip" w={54} h={24} />
        <Skeleton className="sk-chip" w={72} h={24} />
      </div>
      <Skeleton className="sk-uses" h={40} />
    </article>
  );
}

/** The honest "match" — how much of the box this recipe draws on. Calm when none. */
function MatchBadge({ used, total }: { used: number; total: number }) {
  if (total <= 0 || used <= 0) return null;
  return (
    <span className="rc-match">
      <IconBasket />
      <b>{used}</b> van <b>{total}</b> uit je pakket
    </span>
  );
}

function RecipeMeta({ recipe }: { recipe: SuggestedRecipe }) {
  return (
    <p className="rc-meta">
      {recipe.total_minutes != null && (
        <span className="rc-meta-item">
          <IconClock />
          <span className="num">{recipe.total_minutes}</span> min
        </span>
      )}
      <span className="rc-meta-item">
        <IconPlate />
        <span className="num">{recipe.servings}</span> {recipe.servings === 1 ? 'portie' : 'porties'}
      </span>
    </p>
  );
}

function HeartButton({
  title,
  saved,
  onClick,
  className,
}: {
  title: string;
  saved: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`rc-heart${saved ? ' is-saved' : ''}${className ? ` ${className}` : ''}`}
      aria-pressed={saved}
      aria-label={saved ? `${title} is bewaard` : `${title} bewaren`}
      onClick={onClick}
    >
      <IconHeart filled={saved} />
    </button>
  );
}

function SuggestionCard({
  recipe,
  boxTotal,
  saved,
  onOpen,
  onBookmark,
}: {
  recipe: SuggestedRecipe;
  boxTotal: number;
  saved: boolean;
  onOpen: () => void;
  onBookmark: () => void;
}) {
  return (
    <article className="card rc-card">
      <MatchBadge used={recipe.uses_from_box.length} total={boxTotal} />
      <h2 className="rc-title">{recipe.title}</h2>
      <RecipeMeta recipe={recipe} />

      {recipe.tags.length > 0 && (
        <div className="rc-tags" aria-label="Labels">
          {recipe.tags.map((t, j) => (
            <span className="rc-tag" key={`${t}-${j}`}>
              {t}
            </span>
          ))}
        </div>
      )}

      {recipe.uses_from_box.length > 0 && (
        <p className="rc-box">
          <span className="rc-box-label">Uit je pakket:</span> {recipe.uses_from_box.join(', ')}
        </p>
      )}

      <div className="rc-actions">
        <HeartButton title={recipe.title} saved={saved} onClick={onBookmark} />
        <button className="btn btn-primary rc-open" onClick={onOpen}>
          Bekijk recept <IconArrowRight />
        </button>
      </div>
    </article>
  );
}

function RecipeDetailView({
  recipe,
  index,
  boxTotal,
  saved,
  onBack,
  onReplace,
  onBookmark,
}: {
  recipe: SuggestedRecipe;
  index: number;
  boxTotal: number;
  saved: boolean;
  onBack: () => void;
  onReplace: (index: number, next: SuggestedRecipe) => void;
  onBookmark: () => void;
}) {
  const [transcript, setTranscript] = useState<{ role: 'user' | 'ack'; text: string }[]>([]);
  const [instruction, setInstruction] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState('');

  const send = async () => {
    const text = instruction.trim();
    if (!text) return;
    setChatBusy(true);
    setChatError('');
    setTranscript((t) => [...t, { role: 'user', text }]);
    setInstruction('');
    try {
      const { recipe: next } = await adjustRecipe({ recipe, instruction: text });
      onReplace(index, next);
      setTranscript((t) => [...t, { role: 'ack', text: 'Aangepast ✓' }]);
    } catch (err) {
      setChatError(
        isOffline(err) ? 'Geen verbinding — aanpassen lukt alleen online.' : 'Kon het recept niet aanpassen.',
      );
    } finally {
      setChatBusy(false);
    }
  };

  return (
    <>
      <div className="topbar">
        <button className="btn btn-quiet back" onClick={onBack} aria-label="Terug naar resultaten">
          ‹ Resultaten
        </button>
        <h1>Recept</h1>
        <HeartButton title={recipe.title} saved={saved} onClick={onBookmark} className="rc-heart-bar" />
      </div>

      <main className="stack">
        <section className="card rc-detail">
          <MatchBadge used={recipe.uses_from_box.length} total={boxTotal} />
          <h2 className="rc-detail-title">{recipe.title}</h2>
          <RecipeMeta recipe={recipe} />

          {recipe.tags.length > 0 && (
            <div className="rc-tags" aria-label="Labels">
              {recipe.tags.map((t, j) => (
                <span className="rc-tag" key={`${t}-${j}`}>
                  {t}
                </span>
              ))}
            </div>
          )}

          {recipe.uses_from_box.length > 0 && (
            <p className="rc-box">
              <span className="rc-box-label">Uit je pakket:</span> {recipe.uses_from_box.join(', ')}
            </p>
          )}

          <h3 className="rc-subhead">Ingrediënten</h3>
          <ul className="rc-ings">
            {recipe.ingredients.map((ing, j) => (
              <li key={j}>
                <span className="ing-amt num">{amountLabel(ing)}</span>
                <span className="ing-name">
                  {ing.name}
                  {ing.note ? ` (${ing.note})` : ''}
                </span>
              </li>
            ))}
          </ul>

          <h3 className="rc-subhead">Stappen</h3>
          <ol className="rc-steps">
            {recipe.steps.map((s, j) => (
              <li key={j}>{s.text}</li>
            ))}
          </ol>
        </section>

        <section className="card rc-adjust">
          <h3 className="rc-adjust-head">Aanpassen</h3>
          <p className="sheet-note">
            Vraag een variatie: “maak &apos;m veganistisch”, “meer knoflook”, “zonder noten”.
          </p>
          {transcript.length > 0 && (
            <ul className="chat-log">
              {transcript.map((m, j) => (
                <li key={j} className={m.role === 'user' ? 'chat-user' : 'chat-ack'}>
                  {m.text}
                </li>
              ))}
            </ul>
          )}
          <div className="sheet-row">
            <input
              type="text"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder="Bijv. maak 'm veganistisch, meer knoflook…"
              aria-label="Aanpassing"
              disabled={chatBusy}
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-outline"
              aria-label="Stuur"
              onClick={() => void send()}
              disabled={chatBusy || !instruction.trim()}
            >
              {chatBusy ? <DotsMini /> : 'Stuur'}
            </button>
          </div>
          {chatError && <p className="sheet-note error">{chatError}</p>}
        </section>

        <button className="btn btn-primary btn-lg" onClick={onBookmark}>
          Bewaar dit recept
        </button>
      </main>
    </>
  );
}

/* ---------------- icons — stroke SVGs in the house style (weather-icon idiom) ---------------- */

function Svg({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      className="rc-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

function IconBolt() {
  return (
    <Svg>
      <path d="M13 2 L4 14 h6 l-1 8 L20 10 h-6 z" />
    </Svg>
  );
}
function IconSprout() {
  return (
    <Svg>
      <path d="M12 21 V9" />
      <path d="M11 12 C6 12 4 8 4 4 C8 4 11 7 11 12 Z" />
      <path d="M13 12 C18 12 20 8 20 4 C16 4 13 7 13 12 Z" />
    </Svg>
  );
}
function IconFish() {
  return (
    <Svg>
      <path d="M3 12 C7 6 14 6 18 12 C14 18 7 18 3 12 Z" />
      <path d="M18 12 L22 9 V15 Z" />
      <circle cx="8" cy="11" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}
function IconChili() {
  return (
    <Svg>
      <path d="M8 21 C4 20 5 15 9 15 C13 15 16 12 16 7" />
      <path d="M16 7 C16 4 17 3 20 3" />
    </Svg>
  );
}
function IconClock() {
  return (
    <Svg size={16}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5 V12 L15 14" />
    </Svg>
  );
}
function IconPlate() {
  return (
    <Svg size={16}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.4" />
    </Svg>
  );
}
function IconBasket() {
  return (
    <Svg size={16}>
      <path d="M5 8 H19 L17.6 20 A1 1 0 0 1 16.6 21 H7.4 A1 1 0 0 1 6.4 20 Z" />
      <path d="M8.5 8 L11 3 M15.5 8 L13 3" />
    </Svg>
  );
}
function IconHeart({ filled = false }: { filled?: boolean }) {
  return (
    <svg
      className="rc-icon"
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20 C12 20 3.5 14.5 3.5 8.7 A3.7 3.7 0 0 1 12 6.2 A3.7 3.7 0 0 1 20.5 8.7 C20.5 14.5 12 20 12 20 Z" />
    </svg>
  );
}
function IconArrowRight() {
  return (
    <Svg size={18}>
      <path d="M4 12 H19 M13 6 L19 12 L13 18" />
    </Svg>
  );
}
function IconRefresh() {
  return (
    <Svg size={18}>
      <path d="M20 12 A8 8 0 1 1 17.4 6.1" />
      <path d="M17.6 2.8 V6.4 H14" />
    </Svg>
  );
}
function IconSearch() {
  return (
    <Svg size={18}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20 L16.2 16.2" />
    </Svg>
  );
}
function IconCheck() {
  return (
    <Svg size={16}>
      <path d="M5 12.5 L10 17 L19 7" />
    </Svg>
  );
}
function IconPlus() {
  return (
    <Svg size={16}>
      <path d="M12 5 V19 M5 12 H19" />
    </Svg>
  );
}
